import React from 'react';

import { BasicDropdownItem } from '../DropdownItems/BasicDropdownItem';
import { InputDate } from '../InputDate/InputDate';
import { ConnectedInputStake } from '../InputStake/InputStake';
import { MatchForecast, valueof } from '../interfaces/tip/MatchForecast';
import { Tip } from '../interfaces/tip/Tip';
import { MatchFromAPI } from '../interfaces/MatchFromAPI';
import { Bookie, Market, OddsFormat, Sport, Stake } from '../interfaces/utils';

import {
  MatchSwitcher,
  MatchForecastType,
} from '../MatchSwitcher/MatchSwitcher';
import { Box } from '../shared/Box/Box';
import { Column } from '../shared/Column/Column';
import { Grid } from '../shared/Grid/Grid';
import { InputDisplayOnly } from '../shared/InputDisplayOnly/InputDisplayOnly';
import { InputDropdown } from '../shared/InputDropdown/InputDropdown';
import { InputModal } from '../shared/InputModal/InputModal';
import { InputOdds, OddsType } from '../shared/InputOdds/InputOdds';
import { InputText } from '../shared/InputText/InputText';
import { fullDateFormatter } from '../utils/fullDateFormatter';
import { getSportIcon } from '../utils/icons/getSportIcon';

import styles from './Steps.module.scss';
import { CollapsibleBox } from '../shared/CollapsibleBox/CollapsibleBox';
import SVG from 'react-inlinesvg';
import add from '../../../assets/icons/add.svg';
import { tr } from '../../../utils/translations/translator';
import { useTranslation } from 'react-i18next';
import {
  CustomMatchValidation,
  DefaultMatchValidation,
} from '../interfaces/tip/Validations';
import { MarketInputDropdown } from '../shared/InputDropdown/MarketInputDropdown';
import { MarketsBySport } from '../../../store/markets/reducer';
import { MatchAutoSuggestConnected } from '../AutoSuggest/MatchAutoSuggest';
import { CustomAutosuggest } from '../AutoSuggest/CustomAutoSuggest';
import { searchPlayerRequest } from '../../../api/matchPlayers';
import { searchCompetitionRequest } from '../../../api/matchCompetitions';
interface StepOneProps {
  tip: Tip;

  hooks: {
    handleMatchSelection: (
      matchForecast: MatchForecast,
      matchFromAPI: MatchFromAPI,
    ) => void;
    handleMatchSwitcher: (matchForecast: MatchForecast) => void;
    handleTipValue: (key: keyof Tip, value: valueof<Tip>) => void;
    handleMatchForecastValue: (
      matchForecast: MatchForecast,
      key: keyof MatchForecast,
      value: valueof<MatchForecast>,
    ) => void;
    handleFormattedOdds: (matchForecast: MatchForecast, odds: OddsType) => void;
    handleMatchForecastReset: (matchForecast: MatchForecast) => void;
    handleAddMatchForecast: (index: any) => void;
  };

  sports: Sport[];
  featuredSports: Sport[];
  markets: Market[];
  featuredMarkets: Market[];
  bookies: Bookie[];
  featuredBookies: Bookie[];
  featuredStakes: Stake[];
  oddsFormat: OddsFormat;
  marketsBySport: MarketsBySport;
  featuredMarketsBySport: MarketsBySport;
  showValidations: boolean;
}

export const StepOne: React.FC<StepOneProps> = ({
  tip,

  hooks: {
    handleFormattedOdds,
    handleTipValue,
    handleMatchSelection,
    handleMatchSwitcher,
    handleMatchForecastValue,
    handleMatchForecastReset,
    handleAddMatchForecast,
  },

  sports,
  featuredSports,
  markets,
  featuredMarkets,
  marketsBySport,
  featuredMarketsBySport,
  bookies,
  featuredBookies,
  featuredStakes,
  oddsFormat,
  showValidations,
}) => {
  const isCombined = tip.type === 'COMBINED';
  const { t } = useTranslation();

  const renderCustomMatchForecast = (matchForecast: MatchForecast) => {
    const validationsNode = tip.validations.step1.matchForecasts[
      matchForecast.index
    ] as CustomMatchValidation;

    return (
      <>
        <Grid key={'CustomMatchForecast' + matchForecast.index}>
          <Column grow="4">
            <InputText
              label={tr(t, 'team') + ' 1'}
              value={matchForecast.home_player}
              onChange={(home_player) =>
                handleMatchForecastValue(
                  matchForecast,
                  'home_player',
                  home_player,
                )
              }
              validity={showValidations && validationsNode.home_player}
              placeholder="Type here"
            />
          </Column>
          <Column grow="4">
            <InputText
              label={tr(t, 'team') + ' 2'}
              value={matchForecast.away_player}
              onChange={(away_player) =>
                handleMatchForecastValue(
                  matchForecast,
                  'away_player',
                  away_player,
                )
              }
              validity={showValidations && validationsNode.away_player}
              placeholder="Type here"
            />
          </Column>
          <Column grow="4">
            <MatchSwitcher
              onClick={() => handleMatchSwitcher(matchForecast)}
              matchForecastType={matchForecast.type}
            />
          </Column>
        </Grid>

        <Grid>
          <Column grow="4">
            <InputModal
              label={tr(t, 'date')}
              ModalItem={InputDate}
              handleChange={(date) =>
                handleMatchForecastValue(matchForecast, 'start_date', date)
              }
              type="DATE"
              value={matchForecast.getMatchDate()}
              validity={showValidations && validationsNode.start_date}
            />
          </Column>
          <Column grow="4">
            <InputDropdown
              type="sport"
              label={tr(t, 'sport')}
              DropdownTitle={tr(t, 'select-sport')}
              options={sports}
              featuredOptions={featuredSports}
              handleChange={(sport) =>
                handleMatchForecastValue(matchForecast, 'sport', sport)
              }
              value={matchForecast.sport}
              DropdownItem={BasicDropdownItem}
              validity={showValidations && validationsNode.sport}
            />
          </Column>
          <Column grow="4">
            <InputText
              label={tr(t, 'competition')}
              value={matchForecast.competition_name}
              onChange={(competition_name) =>
                handleMatchForecastValue(
                  matchForecast,
                  'competition_name',
                  competition_name,
                )
              }
              placeholder="Type here"
              validity={showValidations && validationsNode.competition_name}
            />
          </Column>
        </Grid>
      </>
    );
  };
  const renderDefaultMatchForecast = (matchForecast: MatchForecast) => {
    const validationsNode = tip.validations.step1.matchForecasts[
      matchForecast.index
    ] as DefaultMatchValidation;
    return (
      <Grid key={'DefaultMatchForecast' + matchForecast.index}>
        <Column grow="8">
          <MatchAutoSuggestConnected
            label={
              isCombined
                ? tr(t, 'match') + ' ' + (matchForecast.index + 1)
                : tr(t, 'match')
            }
            handleChange={(matchFromAPI: MatchFromAPI): void =>
              handleMatchSelection(matchForecast, matchFromAPI)
            }
            matchForecast={matchForecast}
            onDelete={() => handleMatchForecastReset(matchForecast)}
            placeholder={`${tr(t, 'example')} FC Barcelona - Real Madrid"`}
            validity={showValidations && validationsNode.match}
          />
        </Column>

        <Column grow="4">
          <MatchSwitcher
            onClick={() => handleMatchSwitcher(matchForecast)}
            matchForecastType={matchForecast.type}
          />
        </Column>
      </Grid>
    );
  };

  return (
    <>
      {tip.matchForecasts.map((matchForecast: MatchForecast) => {
        return (
          <CollapsibleBox
            isDropdown={isCombined}
            isCustom={matchForecast.type === MatchForecastType.CUSTOM}
            title={tr(t, 'match') + ' ' + (matchForecast.index + 1)}
            key={matchForecast.index}
          >
            <div>
              {matchForecast.type === MatchForecastType.DEFAULT &&
                renderDefaultMatchForecast(matchForecast)}

              {matchForecast.type === MatchForecastType.DEFAULT &&
                !!matchForecast.home_player.length && (
                  <Grid>
                    <Column grow="4">
                      <InputDisplayOnly
                        label={tr(t, 'date')}
                        value={fullDateFormatter(matchForecast.getMatchDate())}
                      />
                    </Column>
                    <Column grow="8">
                      <InputDisplayOnly
                        label={tr(t, 'competition')}
                        icon={getSportIcon(matchForecast.sport.code)}
                        value={matchForecast.competition_name}
                      />
                    </Column>
                  </Grid>
                )}
              {matchForecast.type === MatchForecastType.CUSTOM &&
                renderCustomMatchForecast(matchForecast)}

              <Grid tab>
                <Column grow={isCombined ? '8' : '12'}>
                  <InputText
                    label={tr(t, 'tip')}
                    onChange={(tip: string) =>
                      handleMatchForecastValue(matchForecast, 'tip', tip)
                    }
                    placeholder={`${tr(t, 'example:')} ${tr(
                      t,
                      'winner',
                    )} FC Barcelona`}
                    value={matchForecast.tip}
                    validity={
                      showValidations &&
                      tip.validations.step1.matchForecasts[matchForecast.index]
                        .tip
                    }
                  />
                </Column>
                {isCombined && (
                  <Column grow="4">
                    <InputOdds
                      label={tr(t, 'odds')}
                      onChange={(odds: OddsType) => {
                        handleFormattedOdds(matchForecast, odds);
                      }}
                      value={matchForecast.formatted_odds}
                      placeholder={tr(t, `odds_example_${oddsFormat}`)}
                      oddsFormat={oddsFormat}
                      validity={
                        showValidations &&
                        tip.validations.step1.matchForecasts[
                          matchForecast.index
                        ].formatted_odds
                      }
                    />
                  </Column>
                )}
              </Grid>
              <Grid tab>
                <Column grow="12">
                  <MarketInputDropdown
                    label={tr(t, 'market')}
                    DropdownTitle={tr(t, 'select-market')}
                    markets={markets}
                    marketsBySport={marketsBySport}
                    featuredMarkets={featuredMarkets}
                    featuredMarketsBySport={featuredMarketsBySport}
                    handleChange={(market: Market) => {
                      handleMatchForecastValue(matchForecast, 'market', market);
                    }}
                    value={matchForecast.market}
                    sport={matchForecast.sport}
                    DropdownItem={BasicDropdownItem}
                    validity={
                      showValidations &&
                      tip.validations.step1.matchForecasts[matchForecast.index]
                        .market
                    }
                  />
                </Column>
              </Grid>
            </div>
          </CollapsibleBox>
        );
      })}
      {isCombined && (
        <Box>
          <div
            className={styles.AddMatchBtn}
            onClick={() => handleAddMatchForecast(tip.matchForecasts.length)}
          >
            <label className={styles.label}>
              {tr(t, 'match')} {tip.matchForecasts.length + 1}
            </label>
            <span>
              {tr(t, 'add')} <SVG src={add} />
            </span>
          </div>
        </Box>
      )}
      <Box>
        <Grid>
          <Column grow="2">
            <InputModal
              label="Stake"
              type="STAKE"
              ModalItem={ConnectedInputStake}
              handleChange={(stake) => handleTipValue('stake', stake)}
              value={tip.stake === 0 ? '-' : tip.stake}
              featured={featuredStakes}
              validity={showValidations && tip.validations.step1.stake}
              isSpecialChannelSelected={window.location.pathname.includes('challenge')}
            />
          </Column>
          <Column grow="8">
            <InputDropdown
              label={tr(t, 'bookie')}
              DropdownTitle={tr(t, 'select-bookie')}
              options={bookies}
              featuredOptions={featuredBookies}
              handleChange={(bookie) => handleTipValue('bookie', bookie)}
              value={tip.bookie}
              DropdownItem={BasicDropdownItem}
              type="bookie"
              openUpwards
              validity={showValidations && tip.validations.step1.bookie}
            />
          </Column>

          <Column grow="4">
            <InputOdds
              label={isCombined ? tr(t, 'total-odds') : tr(t, 'odds')}
              onChange={(odds: OddsType) => {
                handleFormattedOdds(tip.matchForecasts[0], odds);
              }}
              value={tip.accumulatedOdds}
              placeholder={tr(t, `odds_example_${oddsFormat}`)}
              oddsFormat={oddsFormat}
              validity={
                showValidations && tip.validations.step1.accumulatedOdds
              }
              disabled={isCombined}
            />
          </Column>
        </Grid>
      </Box>
    </>
  );
};
